import { useId, useMemo, useRef, useState } from 'react';
import {
  CONVERSATION_ATTACHMENTS_MAX,
  CONVERSATION_LOCATION_REFERENCES_MAX,
  CONVERSATION_MESSAGE_MAX,
  placeToken,
  requirementToken,
  type ConversationLocations,
} from '@scoutaround/script-breakdown-shared';

import type { ProjectPerson } from '../../lib/api.js';
import { Icon } from '../Icon.js';
import { locationMetaLine } from '../LocationCard.js';
import { useUploadQueue, type UploadControl, type UploadLimit } from '../upload/useUploadQueue.js';
import { FileChip } from './Attachments.js';
import { shortPlaceName } from './LocationPreview.js';

export interface Mentionable {
  readonly userId: number;
  readonly displayName: string;
}

/** `@Name` as typed → `<@id>` as stored, for the people picked from the list. */
export function encodeMentions(text: string, picked: readonly Mentionable[]): string {
  let out = text;
  // Longest names first, so "Ana Maria" is not eaten by "Ana".
  for (const person of [...picked].sort((a, b) => b.displayName.length - a.displayName.length)) {
    out = out.split(`@${person.displayName}`).join(`<@${String(person.userId)}>`);
  }
  return out;
}

export function mentionablesFrom(
  people: readonly ProjectPerson[] | undefined,
  eligible: (person: ProjectPerson) => boolean,
  me: number | null,
): Mentionable[] {
  return (people ?? [])
    .filter((p) => p.memberStatus === 'ACTIVE' && p.userId !== me && p.displayName !== null && eligible(p))
    .map((p) => ({ userId: p.userId, displayName: p.displayName ?? 'Member' }));
}

/** A place or a script location the writer may point at. */
export interface Referenceable {
  /** `place:123` or `requirement:01J…` — stable across renders. */
  readonly key: string;
  readonly group: 'SCRIPT_LOCATION' | 'PLACE';
  /** What `#` is followed by in the text box; unique within one list. */
  readonly display: string;
  readonly title: string;
  readonly detail: string;
  readonly imageUrl: string | null;
  /** The stored form, `<#place:123>` / `<#requirement:…>`. */
  readonly token: string;
}

/**
 * What the "# Location" picker offers: the production's live script locations,
 * then the places it is considering (a rejected one included — it is still a
 * place somebody may want to talk about). A place no longer in the production
 * and a retired script location are not offered; the server would refuse them.
 */
export function referenceablesFrom(locations: ConversationLocations | undefined): Referenceable[] {
  const out: Referenceable[] = [];
  const used = new Map<string, number>();
  const unique = (name: string) => {
    const seen = (used.get(name) ?? 0) + 1;
    used.set(name, seen);
    return seen === 1 ? name : `${name} ${String(seen)}`;
  };

  for (const s of locations?.scriptLocations ?? []) {
    if (!s.live) continue;
    out.push({
      key: `requirement:${s.requirementId}`,
      group: 'SCRIPT_LOCATION',
      display: unique(s.title.trim()),
      title: s.title,
      detail: `${String(s.sceneCount)} ${s.sceneCount === 1 ? 'scene' : 'scenes'} · ${String(s.placeCount)} ${s.placeCount === 1 ? 'place' : 'places'} found`,
      imageUrl: null,
      token: requirementToken(s.requirementId),
    });
  }
  for (const p of locations?.places ?? []) {
    if (!p.inProduction) continue;
    out.push({
      key: `place:${String(p.locationId)}`,
      group: 'PLACE',
      // Without the chip's ellipsis: this is text somebody is writing.
      display: unique(shortPlaceName(p.title).replace(/…$/, '')),
      title: p.title,
      detail: p.kind === 'POI' ? 'A scout’s find' : locationMetaLine(p),
      imageUrl: p.imageUrl,
      token: placeToken(p.locationId),
    });
  }
  return out;
}

/** `#Name` as typed → its token as stored, for the locations picked from the list. */
export function encodeReferences(text: string, picked: readonly Referenceable[]): string {
  let out = text;
  for (const ref of [...picked].sort((a, b) => b.display.length - a.display.length)) {
    out = out.split(`#${ref.display}`).join(ref.token);
  }
  return out;
}

type Picker = 'person' | 'location' | null;

/** Said when a plan has no media storage, beside the paperclip it disables. */
export const ATTACH_UNAVAILABLE = 'Your plan does not include file uploads.';

/**
 * Files on a message (owner choice B, 2026-11-04). Each file is uploaded as soon as
 * it is picked, under its own key; Send attaches the ones that finished.
 */
export interface AttachOptions {
  /** The server's limit and allowlist (`…/files/meta`); undefined while unknown — the server still checks. */
  readonly limit: UploadLimit | undefined;
  /** False on a plan without media storage: the paperclip is shown disabled, with the reason. */
  readonly allowed: boolean;
  readonly upload: (file: File, clientRequestId: string, control: UploadControl) => Promise<{ id: string }>;
}

/** "jpg,png" → ".jpg,.png" for the file picker; none when the list is unknown. */
function acceptOf(limit: UploadLimit | undefined): string | undefined {
  if (!limit || limit.acceptedExtensions.length === 0) return undefined;
  return limit.acceptedExtensions.map((e) => `.${e.replace(/^\./, '')}`).join(',');
}

/**
 * Writing a message. `@` opens the list of people this conversation may
 * mention — on a private one only its participants (spec §2) — and `#` the
 * production's script locations and places. Picking one writes `@Name` or
 * `#Name`; the stored form is a token (`<@id>`, `<#place:id>`), so a renamed
 * account or listing reads correctly later and a place is always shown as the
 * reader may see it. Cmd/Ctrl+Enter sends.
 */
export function Composer({
  mentionable,
  referenceable = [],
  disabled = false,
  disabledReason = null,
  submitLabel = 'Send',
  placeholder = 'Write a message',
  attach,
  testId,
  onSend,
}: {
  mentionable: readonly Mentionable[];
  referenceable?: readonly Referenceable[];
  disabled?: boolean;
  disabledReason?: string | null;
  submitLabel?: string;
  placeholder?: string;
  /** Without it, the composer takes text only. */
  attach?: AttachOptions;
  testId: string;
  onSend: (body: string, attachmentIds: readonly string[]) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState<Mentionable[]>([]);
  const [pickedRefs, setPickedRefs] = useState<Referenceable[]>([]);
  const [picking, setPicking] = useState<Picker>(null);
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const listId = useId();
  const [planNote, setPlanNote] = useState(false);
  const [overflow, setOverflow] = useState(0);
  const attachRef = useRef(attach);
  attachRef.current = attach;
  const files = useUploadQueue<true>(
    attach?.limit,
    (item, control) => {
      const options = attachRef.current;
      if (!options) return Promise.reject(new Error('Files cannot be attached here.'));
      return options.upload(item.file, item.clientRequestId, control);
    },
    () => undefined,
  );
  const uploaded = files.items.flatMap((i) => (i.state === 'done' && i.resultId !== null ? [i.resultId] : []));
  const uploading = files.summary.active + files.summary.queued > 0;
  const failedFiles = files.summary.failed > 0;

  const pick = (chosen: readonly File[]) => {
    const room = Math.max(0, CONVERSATION_ATTACHMENTS_MAX - files.items.length);
    setOverflow(Math.max(0, chosen.length - room));
    if (room > 0) files.add(chosen.slice(0, room), true);
  };

  const needle = filter.toLowerCase();
  const people = useMemo(
    () => mentionable.filter((m) => m.displayName.toLowerCase().includes(needle)),
    [mentionable, needle],
  );
  const locations = useMemo(
    () =>
      referenceable.filter(
        (r) => r.title.toLowerCase().includes(needle) || r.detail.toLowerCase().includes(needle),
      ),
    [referenceable, needle],
  );

  const open = (which: Picker) => {
    setFilter('');
    setPicking(which);
  };

  /** Replaces the `@`/`#` just typed (if any) with the picked name and a space. */
  const insertAtCursor = (sigil: '@' | '#', name: string) => {
    const el = area.current;
    const at = el?.selectionStart ?? text.length;
    const before = text.slice(0, at);
    const trimmed = before.endsWith(sigil) ? before.slice(0, -1) : before;
    setText(`${trimmed}${sigil}${name} ${text.slice(at)}`);
    setPicking(null);
    setFilter('');
    requestAnimationFrame(() => el?.focus());
  };

  const insertPerson = (person: Mentionable) => {
    insertAtCursor('@', person.displayName);
    setPicked((current) => (current.some((p) => p.userId === person.userId) ? current : [...current, person]));
  };

  const insertLocation = (ref: Referenceable) => {
    insertAtCursor('#', ref.display);
    setPickedRefs((current) => (current.some((r) => r.key === ref.key) ? current : [...current, ref]));
  };

  const send = async () => {
    const body = encodeReferences(encodeMentions(text.trim(), picked), pickedRefs);
    if ((body === '' && uploaded.length === 0) || sending || uploading || failedFiles) return;
    setSending(true);
    setError(null);
    try {
      await onSend(body, uploaded);
      setText('');
      setPicked([]);
      setPickedRefs([]);
      files.reset();
      setOverflow(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The message could not be sent. Try again.');
    } finally {
      setSending(false);
    }
  };

  if (disabled) {
    return (
      <p className="mm" data-testid={`${testId}-disabled`} style={{ margin: 0, fontSize: 13 }}>
        {disabledReason ?? 'You cannot write here.'}
      </p>
    );
  }

  const pickerStyle = {
    border: '1px solid var(--color-neutral-300)',
    background: 'var(--color-bg)',
    padding: 8,
    display: 'grid',
    gap: 6,
  } as const;
  const optionStyle = { width: '100%', textAlign: 'left', border: 'none', minHeight: 32 } as const;
  const groupLabel = { margin: '4px 0 2px', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' } as const;
  const groups: readonly (readonly [string, readonly Referenceable[]])[] = [
    ['Script locations', locations.filter((r) => r.group === 'SCRIPT_LOCATION')],
    ['Places', locations.filter((r) => r.group === 'PLACE')],
  ];

  return (
    <form
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
      style={{ display: 'grid', gap: 8, position: 'relative' }}
    >
      <textarea
        ref={area}
        data-testid={`${testId}-input`}
        aria-label="Message"
        value={text}
        maxLength={CONVERSATION_MESSAGE_MAX}
        rows={3}
        placeholder={placeholder}
        onChange={(event) => {
          const value = event.target.value;
          setText(value);
          if (value.endsWith('@') && mentionable.length > 0) open('person');
          if (value.endsWith('#') && referenceable.length > 0) open('location');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void send();
          }
          if (event.key === 'Escape' && picking !== null) {
            event.stopPropagation();
            setPicking(null);
          }
        }}
        style={{ width: '100%', padding: 10, border: '1px solid var(--color-neutral-300)', fontSize: 14, resize: 'vertical' }}
      />
      {picking === 'person' ? (
        <div data-testid={`${testId}-mention-picker`} style={pickerStyle}>
          <input
            aria-label="Find a person to mention"
            aria-controls={listId}
            data-testid={`${testId}-mention-filter`}
            value={filter}
            autoFocus
            onChange={(event) => {
              setFilter(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setPicking(null);
              }
            }}
            placeholder="Find a person"
            style={{ padding: 6, border: '1px solid var(--color-neutral-300)' }}
          />
          <ul id={listId} style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 180, overflowY: 'auto' }}>
            {people.length === 0 ? (
              <li className="mm" style={{ fontSize: 12.5 }}>Nobody to mention here.</li>
            ) : (
              people.map((person) => (
                <li key={person.userId}>
                  <button
                    type="button"
                    className="ctl"
                    data-testid={`${testId}-mention-option-${String(person.userId)}`}
                    onClick={() => {
                      insertPerson(person);
                    }}
                    style={optionStyle}
                  >
                    {person.displayName}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
      {picking === 'location' ? (
        <div data-testid={`${testId}-location-picker`} style={pickerStyle}>
          <input
            aria-label="Find a location to mention"
            aria-controls={listId}
            data-testid={`${testId}-location-filter`}
            value={filter}
            autoFocus
            onChange={(event) => {
              setFilter(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                setPicking(null);
              }
            }}
            placeholder="Find a script location or a place"
            style={{ padding: 6, border: '1px solid var(--color-neutral-300)' }}
          />
          <div id={listId} style={{ maxHeight: 260, overflowY: 'auto' }}>
            {locations.length === 0 ? (
              <p className="mm" style={{ margin: 0, fontSize: 12.5 }}>
                {referenceable.length === 0 ? 'No locations in this production yet.' : 'Nothing matches that.'}
              </p>
            ) : null}
            {groups.map(([label, group]) =>
              group.length === 0 ? null : (
                <section key={label} aria-label={label}>
                  <p className="mm" style={groupLabel}>{label}</p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {group.map((ref) => (
                      <li key={ref.key}>
                        <button
                          type="button"
                          className="ctl"
                          data-testid={`${testId}-location-option-${ref.key}`}
                          onClick={() => {
                            insertLocation(ref);
                          }}
                          style={{ ...optionStyle, display: 'flex', gap: 10, alignItems: 'center', padding: '4px 6px' }}
                        >
                          {ref.group === 'PLACE' ? (
                            ref.imageUrl ? (
                              <img
                                src={ref.imageUrl}
                                alt=""
                                loading="lazy"
                                style={{ width: 44, height: 32, objectFit: 'cover', flex: 'none', background: 'var(--color-neutral-100)' }}
                              />
                            ) : (
                              <span aria-hidden="true" style={{ width: 44, height: 32, flex: 'none', background: 'var(--color-neutral-100)' }} />
                            )
                          ) : null}
                          <span style={{ display: 'grid', minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ref.title}
                            </span>
                            {ref.detail !== '' ? (
                              <span className="mm" style={{ fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ref.detail}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ),
            )}
          </div>
        </div>
      ) : null}
      {attach && (files.items.length > 0 || files.rejected.length > 0 || overflow > 0) ? (
        <div className="cv-pending" data-testid={`${testId}-files`}>
          <ul className="cv-pending-list">
            {files.items.map((item) => (
              <li key={item.key} data-state={item.state}>
                <FileChip
                  kind={item.file.type.startsWith('image/') ? 'IMAGE' : item.file.type === 'application/pdf' ? 'PDF' : 'DOCUMENT'}
                  filename={item.file.name}
                  byteSize={item.file.size}
                  testId={`${testId}-file-${item.key}`}
                >
                  {item.state === 'uploading' || item.state === 'queued' ? (
                    <span className="mm cv-file-progress" data-testid={`${testId}-file-${item.key}-progress`}>
                      {item.total > 0 ? `${String(Math.floor((item.loaded / item.total) * 100))}%` : 'Waiting'}
                    </span>
                  ) : null}
                  {item.state === 'done' ? (
                    <span className="cv-file-ready" data-testid={`${testId}-file-${item.key}-ready`}>
                      <Icon name="check" size={13} />
                      <span className="sr-only">Uploaded</span>
                    </span>
                  ) : null}
                  {item.state !== 'uploading' ? (
                    <button
                      type="button"
                      className="cv-file-remove"
                      data-testid={`${testId}-file-${item.key}-remove`}
                      aria-label={`Remove ${item.file.name}`}
                      onClick={() => {
                        files.remove(item.key);
                      }}
                    >
                      <Icon name="close" size={12} />
                    </button>
                  ) : null}
                </FileChip>
                {item.state === 'failed' ? (
                  <span className="cv-file-error" role="alert" data-testid={`${testId}-file-${item.key}-error`}>
                    {item.error ?? 'The upload did not finish.'}
                  </span>
                ) : null}
              </li>
            ))}
            {files.rejected.map((r) => (
              <li key={r.key} data-state="refused">
                <span className="cv-file" data-testid={`${testId}-refused-${r.key}`}>
                  <Icon name="alert" size={16} />
                  <span className="cv-file-name">{r.name}</span>
                  <button
                    type="button"
                    className="cv-file-remove"
                    aria-label={`Dismiss ${r.name}`}
                    onClick={() => {
                      files.remove(r.key);
                    }}
                  >
                    <Icon name="close" size={12} />
                  </button>
                </span>
                <span className="cv-file-error">{r.reason}</span>
              </li>
            ))}
          </ul>
          {overflow > 0 ? (
            <p className="cv-file-error" data-testid={`${testId}-files-overflow`}>
              A message can carry {String(CONVERSATION_ATTACHMENTS_MAX)} files. {String(overflow)}{' '}
              {overflow === 1 ? 'was' : 'were'} left out.
            </p>
          ) : null}
          {failedFiles ? (
            <p className="mm cv-pending-note" data-testid={`${testId}-files-failed`}>
              Retry or remove the file that did not upload before you send.{' '}
              <button type="button" className="cv-link-button" data-testid={`${testId}-files-retry`} onClick={files.retryFailed}>
                Retry
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      {attach && planNote && !attach.allowed ? (
        <p className="mm cv-pending-note" data-testid={`${testId}-attach-unavailable`}>
          {ATTACH_UNAVAILABLE}
        </p>
      ) : null}
      {error ? (
        <p role="alert" data-testid={`${testId}-error`} style={{ margin: 0, color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="ctl"
            data-testid={`${testId}-mention`}
            disabled={mentionable.length === 0}
            onClick={() => {
              open(picking === 'person' ? null : 'person');
            }}
            aria-expanded={picking === 'person'}
            title={mentionable.length === 0 ? 'Nobody else can be mentioned here' : 'Mention someone'}
          >
            @ Mention
          </button>
          <button
            type="button"
            className="ctl"
            data-testid={`${testId}-location`}
            disabled={referenceable.length === 0}
            onClick={() => {
              open(picking === 'location' ? null : 'location');
            }}
            aria-expanded={picking === 'location'}
            title={
              referenceable.length === 0
                ? 'This production has no script locations or places yet'
                : `Point at a script location or a place (up to ${String(CONVERSATION_LOCATION_REFERENCES_MAX)} a message)`
            }
          >
            # Location
          </button>
          {attach ? (
            <>
              <input
                ref={picker}
                type="file"
                multiple
                hidden
                accept={acceptOf(attach.limit)}
                data-testid={`${testId}-attach-input`}
                onChange={(event) => {
                  const chosen = [...(event.target.files ?? [])];
                  event.target.value = '';
                  pick(chosen);
                }}
              />
              <button
                type="button"
                className="ctl cv-attach"
                data-testid={`${testId}-attach`}
                aria-disabled={attach.allowed ? undefined : 'true'}
                title={attach.allowed ? `Attach photos or documents (up to ${String(CONVERSATION_ATTACHMENTS_MAX)} a message)` : ATTACH_UNAVAILABLE}
                onClick={() => {
                  if (!attach.allowed) {
                    setPlanNote(true);
                    return;
                  }
                  picker.current?.click();
                }}
              >
                <Icon name="paperclip" size={14} /> Attach
              </button>
            </>
          ) : null}
        </div>
        <button
          type="submit"
          className="ctl"
          data-on="1"
          data-testid={`${testId}-send`}
          disabled={sending || uploading || failedFiles || (text.trim() === '' && uploaded.length === 0)}
          title={uploading ? 'Wait for the files to finish uploading' : undefined}
        >
          {sending ? 'Sending…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
